/* Feb-2026 · Compliance & Licenses Center
 *
 * Finance-facing surface for recording business licenses, state permits,
 * and federal compliance documents. Every doc has a State or Federal
 * jurisdiction, an expiration timer, and an uploaded artifact
 * (PDF/image) that can be viewed in-place from the row's action buttons.
 *
 * Backend: /api/admin/compliance-docs (see routes_admin_compliance_docs.py)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PanelHeader } from './_shared';
import {
  Upload, Eye, Trash2, RefreshCw, ShieldCheck, AlertTriangle,
  Calendar, FileText, Loader2, Landmark, MapPin, X,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC','PR',
];

const STATUS_META = {
  active:         { label: 'Active',         tone: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  expiring_soon:  { label: 'Expiring soon',  tone: 'text-amber-700 bg-amber-50 border-amber-200' },
  expired:        { label: 'Expired',        tone: 'text-rose-700 bg-rose-50 border-rose-200' },
  unknown:        { label: 'No expiry set',  tone: 'text-slate-600 bg-slate-50 border-slate-200' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00Z').toLocaleDateString(); } catch { return iso; }
}

function StatusPill({ status, days_out }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const detail = status === 'expired'
    ? `${Math.abs(days_out || 0)} d overdue`
    : status === 'expiring_soon'
    ? `${days_out ?? 0} d left`
    : status === 'active'
    ? `${days_out ?? 0} d left`
    : null;
  return (
    <span
      data-testid={`compliance-status-${status}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-[1px] text-[11px] font-medium ${meta.tone}`}
    >
      {status === 'expired' && <AlertTriangle className="h-3 w-3" />}
      {meta.label}{detail ? ` · ${detail}` : ''}
    </span>
  );
}

function DocViewer({ doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    let currentUrl = '';
    (async () => {
      try {
        const r = await http.get(`/admin/compliance-docs/${doc.id}/file`, { responseType: 'blob' });
        if (cancelled) return;
        currentUrl = URL.createObjectURL(r.data);
        setBlobUrl(currentUrl);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Unable to load document.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [doc.id]);

  const ct = doc.file?.content_type || '';
  const isImage = ct.startsWith('image/');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="compliance-viewer">
      <div className="relative flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <div>
            <div className="text-sm font-semibold">{doc.title}</div>
            <div className="text-[11px] text-muted-foreground">
              {doc.jurisdiction === 'state' ? `State · ${doc.state || '?'}` : 'Federal'}
              {' · '}{doc.file?.original_filename || 'document'}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="compliance-viewer-close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto bg-muted/30">
          {busy && (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading document…
            </div>
          )}
          {err && (
            <div className="flex h-full items-center justify-center text-sm text-destructive">{err}</div>
          )}
          {!busy && !err && blobUrl && isImage && (
            <img src={blobUrl} alt={doc.title} className="mx-auto max-h-full" />
          )}
          {!busy && !err && blobUrl && !isImage && (
            <iframe src={blobUrl} title={doc.title} className="h-full w-full" />
          )}
        </div>
      </div>
    </div>
  );
}

function UploadForm({ onCreated }) {
  const fileRef = useRef(null);
  const [title, setTitle] = useState('');
  const [jurisdiction, setJurisdiction] = useState('state');
  const [state, setState] = useState('DE');
  const [issuing, setIssuing] = useState('');
  const [number, setNumber] = useState('');
  const [category, setCategory] = useState('license');
  const [issueDate, setIssueDate] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [cadence, setCadence] = useState('annual');
  const [months, setMonths] = useState(12);
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle(''); setIssuing(''); setNumber(''); setIssueDate('');
    setExpiresOn(''); setNotes(''); setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!title.trim()) { toast.error('Title is required.'); return; }
    if (!expiresOn) { toast.error('Set an expiration date so we can time the renewal.'); return; }
    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('jurisdiction', jurisdiction);
    fd.append('state', jurisdiction === 'state' ? state : '');
    fd.append('issuing_authority', issuing.trim());
    fd.append('license_number', number.trim());
    fd.append('category', category.trim() || 'general');
    if (issueDate) fd.append('issue_date', issueDate);
    fd.append('expires_on', expiresOn);
    fd.append('renewal_cadence', cadence);
    fd.append('renewal_months', String(cadence === 'custom_months' ? months : (cadence === 'annual' ? 12 : cadence === 'biennial' ? 24 : 0)));
    fd.append('notes', notes.trim());
    if (file) fd.append('file', file);
    setSaving(true);
    try {
      await http.post('/admin/compliance-docs', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Compliance document saved.');
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="compliance-upload-form">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Upload className="h-4 w-4" /> Record a compliance document
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="text-xs">Title</Label>
          <Input data-testid="compliance-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Delaware Business License" />
        </div>
        <div>
          <Label className="text-xs">Category</Label>
          <Input data-testid="compliance-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="license · permit · registration" />
        </div>
        <div>
          <Label className="text-xs">Jurisdiction</Label>
          <div className="mt-1 flex gap-2">
            <Button size="sm" variant={jurisdiction === 'state' ? 'default' : 'outline'} onClick={() => setJurisdiction('state')} data-testid="compliance-jurisdiction-state">
              <MapPin className="mr-1 h-3.5 w-3.5" /> State
            </Button>
            <Button size="sm" variant={jurisdiction === 'federal' ? 'default' : 'outline'} onClick={() => setJurisdiction('federal')} data-testid="compliance-jurisdiction-federal">
              <Landmark className="mr-1 h-3.5 w-3.5" /> Federal
            </Button>
          </div>
        </div>
        {jurisdiction === 'state' && (
          <div>
            <Label className="text-xs">State</Label>
            <select
              data-testid="compliance-state"
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div>
          <Label className="text-xs">Issuing authority</Label>
          <Input data-testid="compliance-issuer" value={issuing} onChange={(e) => setIssuing(e.target.value)} placeholder="e.g. DE Division of Corporations" />
        </div>
        <div>
          <Label className="text-xs">License / Filing number</Label>
          <Input data-testid="compliance-number" value={number} onChange={(e) => setNumber(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Issue date</Label>
          <Input type="date" data-testid="compliance-issue-date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Expiration / Renewal due</Label>
          <Input type="date" data-testid="compliance-expires" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Renewal cadence</Label>
          <select
            data-testid="compliance-cadence"
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
          >
            <option value="annual">Annual (12 months)</option>
            <option value="biennial">Biennial (24 months)</option>
            <option value="custom_months">Custom months</option>
            <option value="none">One-time (no renewal)</option>
          </select>
        </div>
        {cadence === 'custom_months' && (
          <div>
            <Label className="text-xs">Renewal months</Label>
            <Input type="number" min="1" max="120" value={months} onChange={(e) => setMonths(parseInt(e.target.value || '12', 10))} data-testid="compliance-months" />
          </div>
        )}
        <div className="md:col-span-2">
          <Label className="text-xs">Notes</Label>
          <Textarea rows={2} data-testid="compliance-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the CFO should know before renewal…" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Attach license (PDF, PNG, JPG, WEBP · max 12 MB)</Label>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-primary hover:file:bg-primary/20"
            data-testid="compliance-file"
          />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button onClick={submit} disabled={saving} data-testid="compliance-save-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          Save document
        </Button>
      </div>
    </div>
  );
}

function BulkImportForm({ onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error('Choose a CSV first.'); return; }
    const fd = new FormData();
    fd.append('file', f);
    setBusy(true);
    try {
      const r = await http.post('/admin/compliance-docs/bulk-import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} of ${r.data.created + r.data.errors} rows.`);
      onImported?.();
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk import failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="compliance-bulk-import">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <Upload className="h-4 w-4" /> Bulk import (CSV)
      </div>
      <p className="mb-3 text-[11.5px] text-muted-foreground">
        Columns: <span className="font-mono">title, jurisdiction (state/federal), state, issuing_authority, license_number, category, issue_date, expires_on, renewal_cadence, renewal_months, notes</span>. Files stay attached-later — this seeds metadata only.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-primary hover:file:bg-primary/20"
          data-testid="compliance-bulk-file"
        />
        <Button size="sm" onClick={upload} disabled={busy} data-testid="compliance-bulk-btn">
          {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-2 h-3.5 w-3.5" />}
          Import CSV
        </Button>
      </div>
      {result && result.errors > 0 && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-900">
          <div className="mb-1 font-semibold">{result.errors} row(s) rejected:</div>
          <ul className="list-disc pl-5">
            {(result.error_rows || []).slice(0, 8).map((e, idx) => (
              <li key={idx}>Row {(e.row_index ?? idx) + 1}: {e.error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CompliancePanel() {
  const [docs, setDocs] = useState([]);
  const [tally, setTally] = useState({});
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [viewing, setViewing] = useState(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/compliance-docs', { params: { status: 'all' } });
      setDocs(r.data.docs || []);
      setTally(r.data.by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to load compliance docs.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(
    () => (filter === 'all' ? docs : docs.filter((d) => d.status === filter)),
    [docs, filter],
  );

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? The file remains in archive for audits.`)) return;
    try {
      await http.delete(`/admin/compliance-docs/${doc.id}`);
      toast.success('Deleted.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="space-y-4" data-testid="compliance-panel">
      <PanelHeader
        icon={ShieldCheck}
        title="Compliance & Licenses"
        subtitle="Record state & federal licenses. Every doc runs a renewal timer and pushes red flags to the Admin Overview."
      />

      <div className="flex flex-wrap items-center gap-2">
        {['all', 'active', 'expiring_soon', 'expired'].map((s) => {
          const meta = STATUS_META[s] || { label: 'All' };
          const count = s === 'all' ? docs.length : (tally[s] || 0);
          return (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? 'default' : 'outline'}
              onClick={() => setFilter(s)}
              data-testid={`compliance-filter-${s}`}
            >
              {s === 'all' ? 'All' : meta.label} · {count}
            </Button>
          );
        })}
        <Button size="sm" variant="ghost" onClick={reload} disabled={busy} data-testid="compliance-refresh" className="ml-auto">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <UploadForm onCreated={reload} />

      <BulkImportForm onImported={reload} />

      <div className="overflow-hidden rounded-md border border-border bg-card">
        <table className="w-full text-sm" data-testid="compliance-list">
          <thead className="bg-panel-header/40 text-left text-[11px] uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Jurisdiction</th>
              <th className="px-3 py-2">Number</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No compliance documents yet. Upload your first above.
                </td>
              </tr>
            )}
            {filtered.map((d) => (
              <tr key={d.id} className="border-t border-border" data-testid={`compliance-row-${d.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{d.title}</div>
                  <div className="text-[11px] text-muted-foreground">{d.category || 'general'} · {d.issuing_authority || 'unspecified'}</div>
                </td>
                <td className="px-3 py-2 text-[12px]">
                  {d.jurisdiction === 'state' ? (
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> State · {d.state}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><Landmark className="h-3 w-3" /> Federal</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px]">{d.license_number || '—'}</td>
                <td className="px-3 py-2 text-[12px]">
                  <Calendar className="mr-1 inline h-3 w-3 align-middle" />
                  {fmtDate(d.expires_on)}
                </td>
                <td className="px-3 py-2"><StatusPill status={d.status} days_out={d.days_out} /></td>
                <td className="px-3 py-2 text-right">
                  {d.portal?.url && (
                    <a
                      href={d.portal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mr-1 inline-flex items-center gap-1 rounded-md border border-primary/40 px-2 py-1 text-[11px] text-primary hover:bg-primary/10"
                      data-testid={`compliance-portal-${d.id}`}
                      title={d.portal.label}
                    >
                      Renew at {d.jurisdiction === 'state' ? d.state : 'portal'}
                    </a>
                  )}
                  {d.file ? (
                    <Button size="sm" variant="ghost" onClick={() => setViewing(d)} data-testid={`compliance-view-${d.id}`}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> View
                    </Button>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">
                      <FileText className="mr-1 inline h-3 w-3" /> No file
                    </span>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(d)} data-testid={`compliance-delete-${d.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewing && <DocViewer doc={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
